import { mapKeys, camelCase } from 'lodash';

import { decodeToken } from 'actions/user';

import { CONTEXT_MODE_ELEMENTARY } from 'components/Common/LayoutV2/utils';
import {
  JWT,
  REFRESH_TOKEN,
  DEMO_STUDENT_JWT,
  DEMO_STUDENT_REFRESH_TOKEN,
} from 'constants/system';

import client from 'core/axios';
import { stopCaptureNewTabClicks } from 'routes/hooks/useStudentDemoCheck';

const DEMO_STUDENT_SKIP_ONBOARDING_STORAGE_KEY =
  'loggedInDemoStudentSkipOnboarding';

export const populateContext = data => ({
  type: 'STUDENT_DEMO_ACCOUNT.POPULATE_CONTEXT',
  data,
});

export const getContext = studentScope => async dispatch => {
  try {
    const { status, data } = await client.get(
      `v1/sl_users/student-preview-scope-context/`,
      { params: { studentScope } },
    );
    if (status === 200)
      return dispatch(populateContext({ ...data, studentScope }));
  } catch (e) {
    console.warn('could not get demo student context', e);
  }
  return null;
};

export const populateStudentInfo = data => ({
  type: 'STUDENT_DEMO_ACCOUNT.POPULATE_STUDENT_INFO',
  data,
});

export const checkGradeAndSchool =
  ({ gradeSelection, schoolSelection }) =>
  async dispatch => {
    try {
      const { status, data: { user } = {} } = await client.post(
        `v1/sl_users/student-preview-check/`,
        {
          gradeYear: gradeSelection,
          school: schoolSelection,
        },
      );
      if (status === 200) {
        dispatch(populateStudentInfo(user));
        return user.pk;
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        dispatch(
          populateStudentInfo({
            ...mapKeys(e.response.data, (v, k) => camelCase(k)),
            pk: null,
          }),
        );
      } else {
        console.warn('could not check grade and school combination', e);
      }
    }
    return null;
  };

export const updateStudentInfo = ({ field, value }) => ({
  type: 'STUDENT_DEMO_ACCOUNT.UPDATE_STUDENT_INFO',
  field,
  value,
});

const persistStudentPreviewCounselor = data => {
  localStorage.setItem('studentPreviewCounselor', JSON.stringify(data));
};

const switchToStudentJwt = data => {
  sessionStorage.setItem(`SESSION_${JWT}_KEY`, DEMO_STUDENT_JWT);
  sessionStorage.setItem(
    `SESSION_${REFRESH_TOKEN}_KEY`,
    DEMO_STUDENT_REFRESH_TOKEN,
  );
  localStorage.setItem(DEMO_STUDENT_JWT, data?.demoStudentJwt);
  localStorage.setItem(DEMO_STUDENT_REFRESH_TOKEN, data?.demoStudentJwtRefresh);
};

const switchToCounselorJwt = () => {
  // unsubscribes from event listeners that allow newly opened tabs to stay in student demo mode
  // check routes/hooks/useStudentDemoCheck for full logic
  stopCaptureNewTabClicks();
  sessionStorage.setItem(`SESSION_${JWT}_KEY`, JWT);
  sessionStorage.setItem(`SESSION_${REFRESH_TOKEN}_KEY`, REFRESH_TOKEN);
  window.location = '/';
};

// eslint-disable-next-line consistent-return
const createNewOrResetDemoAccount = async (payload, studentScope) => {
  try {
    const response = await client.post(
      `v1/sl_users/student-preview/`,
      payload,
      { params: { studentScope } },
    );
    const {
      status,
      data: { user: { studentPreviewCounselor } = {} } = {},
      data,
    } = response;
    if (status === 200) {
      switchToStudentJwt(data);
      persistStudentPreviewCounselor(studentPreviewCounselor);
    }
    return response;
  } catch (e) {
    console.warn('could not create demo student account', e);
  }
};

export const createDemoAccountAction =
  ({
    firstName,
    lastName,
    profilePicHash,
    gradeSelection,
    schoolSelection,
    skipOnboarding,
  }) =>
  async dispatch => {
    try {
      const payload = {
        firstName,
        lastName,
        picture: profilePicHash,
        gradeYear: gradeSelection,
        school: schoolSelection,
        skipOnboarding,
      };
      const {
        status,
        data: { user },
      } = await createNewOrResetDemoAccount(payload, CONTEXT_MODE_ELEMENTARY);
      if (status === 200) {
        dispatch(populateStudentInfo(user));
        return user.pk;
      }
    } catch (e) {
      console.warn('could not check grade and school combination', e);
    }
    return null;
  };

const updateDemoAccount = async (payload, studentPk) => {
  try {
    const {
      status,
      data: { user: { studentPreviewCounselor } = {} } = {},
      data,
    } = await client.patch(
      `v1/sl_users/student-preview-login/${studentPk}`,
      payload,
    );
    if (status === 200) {
      switchToStudentJwt(data);
      persistStudentPreviewCounselor(studentPreviewCounselor);
    }
  } catch (e) {
    console.warn('could not update demo student account', e);
  }
};

export const loginDemoAccount =
  (
    {
      studentPk,
      firstName,
      lastName,
      profilePicHash,
      gradeSelection,
      schoolSelection,
      skipOnboarding,
      isReset,
      school,
    },
    studentScope,
  ) =>
  async dispatch => {
    try {
      const payload = {
        firstName,
        lastName,
        picture: profilePicHash,
      };
      if (studentPk && !isReset) {
        await updateDemoAccount(payload, studentPk);
      } else {
        await createNewOrResetDemoAccount(
          {
            ...payload,
            gradeYear: gradeSelection,
            school: schoolSelection,
            skipOnboarding,
          },
          studentScope,
        );
      }
      dispatch(decodeToken());
      localStorage.setItem(
        'loggedInDemoStudentSchool',
        JSON.stringify(school || {}),
      );
      localStorage.setItem('loggedInDemoStudentGrade', gradeSelection);
      localStorage.setItem(
        DEMO_STUDENT_SKIP_ONBOARDING_STORAGE_KEY,
        skipOnboarding,
      );
      localStorage.setItem('loggedInDemoStudentFirstName', firstName);
      localStorage.setItem('loggedInDemoStudentLastName', lastName);
      localStorage.setItem('demoStudentGrade', gradeSelection);
      localStorage.setItem('demoStudentSkipOnboarding', skipOnboarding);
      localStorage.setItem('demoStudentFirstName', firstName);
      localStorage.setItem('demoStudentLastName', lastName);
    } catch (e) {
      console.warn('could not login to demo student account', e);
    }
    return null;
  };

export const switchToCounselorAccount = () => async () => {
  switchToCounselorJwt();
};

export const resetStudentInfo = (isDemoStudent, demoStudentPk) => dispatch => {
  const school = JSON.parse(
    localStorage.getItem('loggedInDemoStudentSchool') || '{}',
  );
  const grade = localStorage.getItem('loggedInDemoStudentGrade');
  const skipOnboarding = localStorage.getItem(
    DEMO_STUDENT_SKIP_ONBOARDING_STORAGE_KEY,
  );
  const firstName = localStorage.getItem('loggedInDemoStudentFirstName');
  const lastName = localStorage.getItem('loggedInDemoStudentLastName');
  dispatch(
    populateStudentInfo({
      school,
      grade: grade ? +grade : undefined,
      skipOnboarding,
      firstName,
      lastName,
    }),
  );
  if (isDemoStudent) {
    dispatch(updateStudentInfo({ field: 'pk', value: demoStudentPk }));
    localStorage.removeItem(DEMO_STUDENT_SKIP_ONBOARDING_STORAGE_KEY);
  }
};
